import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, LocateFixed, Loader2, Send, X, ReceiptText } from "lucide-react";
import { api, errMsg, money, fdate, compressImage } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, Row, Hash, SimBadge, StatusBadge } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";
import { useUserLocation, placeLabel } from "@/lib/location";

export function FineTicket({ fine }) {
  const [phone, setPhone] = useState(fine.phone || "");
  const [sending, setSending] = useState(false);
  const url = `${window.location.origin}/verificar/${fine.folio}`;
  const send = async () => {
    setSending(true);
    try { await api.post(`/fines/${fine.folio}/send`, { phone }); toast.success("Boleta enviada por WhatsApp"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return (
    <div className="space-y-4" data-testid="fine-ticket">
      <div className="rounded-xl border-2 border-dashed p-4 bg-white">
        <div className="flex items-center justify-between">
          <div><div className="text-xs text-muted-foreground">Gobierno de Quintana Roo</div><div className="font-semibold">Boleta de infracción</div></div>
          <StatusBadge status={fine.status} />
        </div>
        <div className="flex gap-4 mt-3 items-start">
          <div className="bg-white p-1.5 border rounded-md"><QRCodeSVG value={url} size={110} fgColor="#16202B" data-testid="fine-qr" /></div>
          <div className="text-sm space-y-1 min-w-0">
            <div className="font-mono text-xs font-semibold" data-testid="fine-ticket-folio">{fine.folio}</div>
            <div>{fine.reason}</div>
            <div className="text-xs text-muted-foreground">{fine.article}</div>
            <div className="font-mono font-semibold">{money(fine.amount)}</div>
          </div>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <Row label="Infractor">{fine.offender_name}</Row>
          {fine.plate && <Row label="Placa"><span className="font-mono text-xs">{fine.plate}</span></Row>}
          <Row label="Ubicación"><span className="font-mono text-xs">{fine.lat?.toFixed(5)}, {fine.lng?.toFixed(5)}</span></Row>
          <Row label="Emitida">{fdate(fine.created_at)} · {fine.agent_name}</Row>
          <Row label="Hash"><Hash value={fine.data_hash} n={8} /></Row>
          {fine.signature && <Row label="Firma"><span className="text-xs">{fine.signature.algorithm}</span></Row>}
          {fine.anchor && <Row label="Tx Polygon"><span className="flex gap-1 items-center"><Hash value={fine.anchor.tx_hash} n={6} /><SimBadge /></span></Row>}
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+529981234567" data-testid="fine-send-phone-input" />
        <Button onClick={send} disabled={sending || !phone} data-testid="fine-send-whatsapp-button">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="ml-1">WhatsApp</span></Button>
      </div>
      <Button variant="outline" className="w-full" onClick={() => window.open(url, "_blank")} data-testid="fine-open-verify-button">Abrir verificación pública</Button>
    </div>
  );
}

export default function Field() {
  const reasons = useFetch("/fines/reasons");
  const [f, setF] = useState({ reason: "", offender_name: "", plate: "", phone: "", notes: "" });
  const [pos, setPos] = useState(null);
  const [fly, setFly] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);
  const loc = useUserLocation();
  const [manualPin, setManualPin] = useState(false);
  const locating = loc.status === "locating";

  useEffect(() => {
    if (loc.position && !manualPin) { setPos(loc.position); setFly((f) => f || loc.position); }
  }, [loc.coords?.lat, loc.coords?.lng, manualPin]); // eslint-disable-line

  const locate = () => {
    setManualPin(false);
    if (loc.position) { setPos(loc.position); setFly([...loc.position]); }
    loc.request();
    if (loc.status === "denied") toast.error("Ubicación bloqueada: actívala en el navegador o toca el mapa");
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/fines", { ...f, lat: pos[0], lng: pos[1], photo });
      setIssued(data);
      toast.success(`Multa ${data.folio} emitida`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const reason = (reasons.data || []).find((r) => r.id === f.reason);
  const ready = f.reason && f.offender_name.length >= 3 && pos && photo;

  if (issued) {
    return (
      <div className="max-w-md mx-auto fade-up">
        <PageHeader eyebrow="Campo" title="Boleta emitida" subtitle="Muestra el QR al infractor o envíala por WhatsApp: cero papel." />
        <Card><CardContent className="p-4"><FineTicket fine={issued} /></CardContent></Card>
        <Button variant="ghost" className="w-full mt-3" onClick={() => { setIssued(null); setPhoto(null); setF({ reason: "", offender_name: "", plate: "", phone: "", notes: "" }); }} data-testid="fine-new-another-button">Emitir otra multa</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Agente de campo" title="Emitir multa" subtitle="Foto + ubicación + motivo. La boleta se firma digitalmente y se ancla al instante." />
      <div className="grid gap-4 md:grid-cols-2">
        <Card><CardContent className="p-5 space-y-3">
          <div className="space-y-1.5"><Label>Motivo</Label>
            <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
              <SelectTrigger data-testid="fine-reason-select"><SelectValue placeholder="Selecciona el motivo" /></SelectTrigger>
              <SelectContent>{(reasons.data || []).map((r) => <SelectItem key={r.id} value={r.id} data-testid={`fine-reason-${r.id}`}>{r.label} · {money(r.amount)}</SelectItem>)}</SelectContent>
            </Select>
            {reason && <div className="text-xs text-muted-foreground">{reason.article}</div>}
          </div>
          <div className="space-y-1.5"><Label>Nombre del infractor</Label><Input value={f.offender_name} onChange={(e) => setF({ ...f, offender_name: e.target.value })} data-testid="fine-offender-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Placa</Label><Input value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} className="uppercase font-mono" data-testid="fine-plate-input" /></div>
            <div className="space-y-1.5"><Label>WhatsApp (opcional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+52998…" data-testid="fine-phone-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Observaciones</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="fine-notes-input" /></div>
          <div className="space-y-1.5"><Label>Evidencia fotográfica (obligatoria)</Label>
            {photo ? (
              <div className="relative w-fit"><img src={photo} alt="evidencia" className="h-36 rounded-lg border object-cover" data-testid="fine-photo-preview" />
                <button className="absolute top-1 right-1 rounded-full bg-white p-1 shadow" onClick={() => setPhoto(null)} aria-label="Quitar"><X className="h-4 w-4" /></button></div>
            ) : (
              <label className="flex items-center gap-3 rounded-lg border-2 border-dashed p-4 cursor-pointer hover:bg-secondary transition-colors">
                <Camera className="h-5 w-5 text-primary" /><span className="text-sm">Tomar foto</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="fine-photo-input"
                  onChange={async (e) => { const file = e.target.files?.[0]; if (file) setPhoto(await compressImage(file, 1024)); }} />
              </label>
            )}
          </div>
        </CardContent></Card>
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Ubicación de la infracción</CardTitle>
            <Button size="sm" variant="outline" onClick={locate} disabled={locating} data-testid="fine-locate-button">{locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}</Button>
          </CardHeader>
          <CardContent>
            <GeoMap className="h-[320px]" testid="fine-map" center={[21.1619, -86.8515]} zoom={13} pitch={0} bearing={0} compact
              onPick={(c) => { setManualPin(true); setPos(c); }} picked={pos} flyTo={fly} flyZoom={17} />
            <div className="text-xs font-mono text-muted-foreground mt-2" data-testid="fine-coordinates">{pos ? `${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}${!manualPin && loc.coords ? ` · GPS ±${loc.coords.accuracy} m` : " · pin manual"}` : "Toca el mapa o usa GPS"}</div>
            {!manualPin && loc.place && <div className="text-xs mt-1 text-[#006A7C]" data-testid="fine-detected-address">{placeLabel(loc.place)}</div>}
            <Button className="w-full mt-4 btn-press" disabled={!ready || saving} onClick={submit} data-testid="fine-submit-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ReceiptText className="h-4 w-4 mr-1" />}Emitir boleta digital {reason ? `· ${money(reason.amount)}` : ""}
            </Button>
            {!ready && <div className="text-xs text-muted-foreground mt-2">Requiere motivo, nombre, ubicación y foto.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
